// MainApp.js
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import './styles/darkSurface.css';
import TabNavigation from './TabNavigation';
import LessonTemplate from './LessonTemplate';
import PlannerBasicPage from './features/plannerBasic/pages/PlannerBasicPage';
import TemplateSelectionScreen from './components/TemplateSelectionScreen';
import AppErrorBoundary from './components/AppErrorBoundary';

// The prompt generator builds its own TipTap editor, and the tab it lives in
// is kept mounted (hidden with display:none) so its state survives a switch.
// Mounted eagerly that put the whole editor stack in front of every visitor,
// so it now loads the first time the tab is opened and stays mounted after.
const BopppsPromptGenerator = lazy(() => import('./BopppsPromptGenerator'));

function AppShell() {
  const [activeTab, setActiveTab] = useState('lesson-template');
  // Once the prompt generator has been opened it stays mounted, so anything
  // typed into it survives switching tabs — the same as before it was lazy.
  const [hasOpenedPromptGenerator, setHasOpenedPromptGenerator] = useState(false);

  useEffect(() => {
    if (activeTab === 'prompt-generator') setHasOpenedPromptGenerator(true);
  }, [activeTab]);
  const [templateData, setTemplateData] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showMigrationNotice, setShowMigrationNotice] = useState(false);
  const [showTemplateSelection, setShowTemplateSelection] = useState(true);
  const [lightMode, setLightMode] = useState(() => localStorage.getItem('boppps-light-mode') === 'true');

  const handleToggleLightMode = () => {
    setLightMode(prev => {
      const next = !prev;
      localStorage.setItem('boppps-light-mode', String(next));
      return next;
    });
  };

  const handleSetLightMode = (value) => {
    setLightMode(value);
    localStorage.setItem('boppps-light-mode', String(value));
  };

  // Check if user has seen the disclaimer
  useEffect(() => {
    const hasSeenDisclaimer = localStorage.getItem('boppps-disclaimer-seen');
    if (!hasSeenDisclaimer) {
      setShowDisclaimer(true);
    }
    const hasDismissedMigrationNotice = localStorage.getItem('lessonplannerone-domain-migration-dismissed');
    if (!hasDismissedMigrationNotice) {
      setShowMigrationNotice(true);
    }
  }, []);

  // Add warning when user tries to refresh/leave the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Remember to export your work! All changes are stored locally in your browser and will be lost if you don\'t export.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleLoadTemplate = (data) => {
    setTemplateData(data);
    setActiveTab('lesson-template');
  };

  const handleDisclaimerAccept = () => {
    localStorage.setItem('boppps-disclaimer-seen', 'true');
    setShowDisclaimer(false);
  };

  const handleMigrationNoticeDismiss = () => {
    localStorage.setItem('lessonplannerone-domain-migration-dismissed', 'true');
    setShowMigrationNotice(false);
  };

  const handleSelectTemplate = (selectedTemplateData) => {
    setTemplateData(selectedTemplateData);
    setShowTemplateSelection(false);
    setActiveTab('lesson-template');
  };

  const handleImportTemplate = (importedData) => {
    setTemplateData(importedData);
    setShowTemplateSelection(false);
    setActiveTab('lesson-template');
  };

  const handleShowTemplateSelection = () => {
    setShowTemplateSelection(true);
  };

  // Show template selection screen if enabled
  if (showTemplateSelection) {
    return (
      <>
        {showDisclaimer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            {/* Scrolls inside itself: at 630px tall this panel is taller than a
                phone held in landscape, and the "I Understand" button — the only
                way past this screen — sat below the bottom of the display. */}
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 sm:p-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
              <div className="mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                  Important Information About Data Storage
                </h2>
                <div className="text-gray-700 space-y-3 text-sm leading-relaxed">
                  <p>
                    <strong>Please note:</strong> All work created in this application is saved exclusively in your local browser storage.
                    No data is transmitted to or stored on our servers.
                  </p>
                  <p>
                    This means:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Your data is private and remains on your device</li>
                    <li>Clearing your browser data or using a different browser will result in loss of your work</li>
                    <li>You must use the export functions to save your work externally</li>
                    <li>You must load your exported JSON files to continue working on saved lessons</li>
                  </ul>
                  <p className="font-semibold text-blue-900 bg-blue-50 p-3 rounded-lg mt-4">
                    Always export your work regularly to prevent data loss.
                  </p>
                </div>
              </div>
              <button
                onClick={handleDisclaimerAccept}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md"
              >
                I Understand
              </button>
            </div>
          </div>
        )}
        {showMigrationNotice && (
          <div className="max-w-6xl mx-auto px-6 pt-4">
            <div className="bg-amber-50 border border-amber-200 text-amber-950 rounded-lg p-4 shadow-sm flex items-start justify-between gap-4">
              <p className="text-sm leading-relaxed">
                <strong>Moving from the old BOPPPS site?</strong> Work saved in your browser on bopppslessonplanner.com does not automatically appear on lessonplannerone.com. Export JSON from the old BOPPPS site and import it here; existing BOPPPS JSON files remain supported. No automatic cross-domain migration or data transfer is performed.
              </p>
              <button
                type="button"
                onClick={handleMigrationNoticeDismiss}
                className="shrink-0 text-sm font-semibold text-amber-900 hover:text-amber-700"
                aria-label="Dismiss domain migration notice"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        <AppErrorBoundary area="the launch screen">
          <TemplateSelectionScreen
            onSelectTemplate={handleSelectTemplate}
            onImportTemplate={handleImportTemplate}
          />
        </AppErrorBoundary>
      </>
    );
  }

  return (
    // The shell background follows the theme too — it used to be bg-gray-50 in
    // both modes, which showed through on any tab that does not paint its own
    // full-height background.
    <div className={`min-h-screen ${lightMode ? 'light-mode bg-gray-50' : 'bg-slate-950'}`}>
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          {/* Scrolls inside itself — see the copy of this dialog above. */}
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 sm:p-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                Important Information About Data Storage
              </h2>
              <div className="text-gray-700 space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>Please note:</strong> All work created in this application is saved exclusively in your local browser storage.
                  No data is transmitted to or stored on our servers.
                </p>
                <p>
                  This means:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your data is private and remains on your device</li>
                  <li>Clearing your browser data or using a different browser will result in loss of your work</li>
                  <li>You must use the export functions to save your work externally</li>
                  <li>You must load your exported JSON files to continue working on saved lessons</li>
                </ul>
                <p className="font-semibold text-blue-900 bg-blue-50 p-3 rounded-lg mt-4">
                  Always export your work regularly to prevent data loss.
                </p>
              </div>
            </div>
            <button
              onClick={handleDisclaimerAccept}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md"
            >
              I Understand
            </button>
          </div>
        </div>
      )}
      <TabNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onShowTemplateSelection={handleShowTemplateSelection}
        lightMode={lightMode}
        onToggleLightMode={handleToggleLightMode}
        onSetLightMode={handleSetLightMode}
      />
      {/* A boundary per tab rather than one around the lot: every tab stays
          mounted so its state survives a switch, and a crash in a hidden one
          would otherwise take the visible tab and the tab bar down with it. */}
      <div className="content">
        <div style={{ display: activeTab === 'prompt-generator' ? 'block' : 'none' }}>
          {hasOpenedPromptGenerator && (
            <AppErrorBoundary area="the prompt generator">
              <Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading the prompt generator…</div>}>
                {/* The deck chosen or uploaded on the launch page, so the prompt
                    can be told to match the instructor's own template. */}
                <BopppsPromptGenerator
                  onLoadTemplate={handleLoadTemplate}
                  lightMode={lightMode}
                  currentDeck={templateData}
                />
              </Suspense>
            </AppErrorBoundary>
          )}
        </div>
        <div style={{ display: activeTab === 'lesson-template' ? 'block' : 'none' }}>
          <AppErrorBoundary area="the slide editor">
            <LessonTemplate
              initialData={templateData}
              onLoadPresentation={handleImportTemplate}
              activeAppTab={activeTab}
              lightMode={lightMode}
              onToggleLightMode={handleToggleLightMode}
            />
          </AppErrorBoundary>
        </div>
        <div style={{ display: activeTab === 'template-settings' ? 'block' : 'none' }}>
          <AppErrorBoundary area="template settings">
            <LessonTemplate
              initialData={templateData}
              onLoadPresentation={handleImportTemplate}
              showSettingsPage={true}
              activeAppTab={activeTab}
              lightMode={lightMode}
              onToggleLightMode={handleToggleLightMode}
            />
          </AppErrorBoundary>
        </div>
        <div style={{ display: activeTab === 'planner-basic' ? 'block' : 'none' }}>
          <AppErrorBoundary area="the planner">
            <PlannerBasicPage lightMode={lightMode} />
          </AppErrorBoundary>
        </div>
      </div>
    </div>
  );
}

export default function MainApp() {
  return (
    <BrowserRouter basename="/app">
      <AppShell />
    </BrowserRouter>
  );
}